import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { resolveProjectPartners, stripPartnerSnapshot } from "@utils/resolvePartner";
import Button from "@components/ui/buttons/Button";
import { useState, useRef, useEffect, useCallback } from "react";
import { usersApi } from "@services/api/users";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import { ContactInfos, PartnerInfos, Project } from "types";
import { updateNested, updateNestedArray } from "helpers/updateHelpers";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import { MemberAssignmentBlock } from "@components/ui/blocks/MemberAssignmentBlock";
import { CreateContactModal } from "@components/modals/CreateContactModal";
import { AddPartnerToProjectModal } from "@components/modals/AddPartnerToProjectModal";
import { ModifyPartnerModal } from "@components/modals/ModifyPartnerModal";
import { AddDecennaleModal } from "@components/modals/AddDecennaleModal";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import { compressImage } from "@utils/compressImage";
import { invitationsApi } from "@services/api/invitations";
import { getFlutterTime } from "@services/timeHelpers";
import { navigateTo } from "context/navigation.service";
import { default_themes, default_folders } from "@screens/sampleData";
import { projectsApi } from "@services/api";
import DropDown from "@components/ui/dropdown/DropDown";
import { PROJECT_PARTNER_CATEGORY_OPTIONS, addSectionToCompanyTypes } from "@constants/partnerTypes";
import { geocodeAddress } from "@helpers/geocodingHelper";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { Timestamp } from "firebase/firestore";
import { billingTemplatesApi, BillingTemplate } from "@services/api/billingTemplates";
import { ProjectBilling } from "types/project";
import HelpTooltip from "@components/ui/HelpTooltip";
import { syncDecennalesFolder } from "@helpers/decennalesHelpers";

// Fix default marker icons broken by webpack — same hack as in the other map modals.
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const FRANCE_CENTER: [number, number] = [46.8, 2.3];

// Recenter imperatively when coordinates change from outside the map
// (e.g. after a successful geocode or a "Recentrer depuis l'adresse" click).
function MapRecenter({ position }: { position: [number, number] | null }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, Math.max(map.getZoom(), 14));
  }, [position, map]);
  return null;
}

function MapClickHandler({ onClick }: { onClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/**
 * Imperatively exposes "place a marker at the current map center" so
 * users can drop a point without having to know an address — useful when
 * geocoding fails or the project location isn't a postal address.
 */
function MapCenterDropper({
  registerDrop,
}: {
  registerDrop: (fn: () => void) => void;
}) {
  const map = useMap();
  useEffect(() => {
    registerDrop(() => {
      const c = map.getCenter();
      // Bubble through onClick path the rest of the map relies on.
      map.fire("click", { latlng: c });
    });
  }, [map, registerDrop]);
  return null;
}

const createEmptyProject = (authorId: string, orgId: string): Project => {
  const now = getFlutterTime();
  return {
    id: `project_${Date.now()}`,
    infos: {
      general: {
        name: "",
        num: "",
        city: "",
        postal: "",
        address: "",
        creation_date: now,
        last_updated: now,
      },
      client: {
        contacts: [{ first_name: "", last_name: "", prefix: "M." as const }],
      },
      team: {},
      partners: [],
    },
    status: "in_progress",
    org_id: orgId,
    author_id: authorId,
    history_events: [],
    themes: structuredClone(default_themes),
    photos: [],
    folders: structuredClone(default_folders),
  };
};

export const CreateProjectModal = ({ onClose }: { onClose: () => void }) => {
  const addProject = useProjectStore((state) => state.addProject);
  const projectTemplates = useProjectStore((state) => state.projectTemplates);
  const partners = useProjectStore((state) => state.partners);
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);

  const firstTemplate = projectTemplates[0];
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(firstTemplate?.id ?? "");
  const [localProject, setLocalProject] = useState<Project>(() => {
    const base = createEmptyProject(userData?.id || "unknown", userData?.org_id || "org1");
    if (firstTemplate) {
      base.themes = structuredClone(firstTemplate.themes);
      base.folders = structuredClone(firstTemplate.folders);
    }
    // Auto-fill team from org default_team settings
    const defaultTeam = orgData?.config?.default_team;
    if (defaultTeam) {
      base.infos.team = {
        ...base.infos.team,
        ...(defaultTeam.dessinateur_id ? { dessinateur_id: defaultTeam.dessinateur_id } : {}),
        ...(defaultTeam.sav_id ? { sav_id: defaultTeam.sav_id } : {}),
        ...(defaultTeam.comptability_id ? { comptability_id: defaultTeam.comptability_id } : {}),
      };
    }
    // Auto-assign commercial to current user
    if (defaultTeam?.auto_assign_commercial !== false && userData?.id) {
      base.infos.team = { ...base.infos.team, commercial_id: userData.id };
    }
    // By default in the creation flow, every assigned team member is visible
    // to the client EXCEPT the dessinateur, who is always hidden by default.
    // The user can toggle any of them on/off via the eye icon before saving.
    const teamIds = [
      base.infos.team.commercial_id,
      base.infos.team.conductor_id,
      base.infos.team.sav_id,
      base.infos.team.comptability_id,
    ].filter((id): id is string => !!id);
    base.infos.team.visible_member_ids = Array.from(new Set(teamIds));
    return base;
  });
  const [activeTab, setActiveTab] = useState<string>("Général");
  const [showCreateContact, setShowCreateContact] = useState<boolean>(false);
  const [addPartnerForSection, setAddPartnerForSection] = useState<string | false>(false);
  const [modifyPartnerId, setModifyPartnerId] = useState<string | null>(null);
  const modifyPartner = modifyPartnerId ? partners.find((p) => p.id === modifyPartnerId) : null;
  const [decennalePartner, setDecennalePartner] = useState<{ id: string; name: string } | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [billingTemplates, setBillingTemplates] = useState<BillingTemplate[]>([]);
  const [selectedBillingTemplateId, setSelectedBillingTemplateId] = useState<string>("");
  const [montantTotal, setMontantTotal] = useState<string>("");
  const [billingPctOverrides, setBillingPctOverrides] = useState<Record<number, string>>({});
  const [billingLabelOverrides, setBillingLabelOverrides] = useState<Record<number, string>>({});
  const [coverPhoto, setCoverPhoto] = useState<File | null>(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState<string | null>(null);
  const coverPhotoInputRef = useRef<HTMLInputElement>(null);

  type UserSuggestion = {
    id: string;
    display_name: string;
    email: string;
    photo_url?: string;
  };
  const [contactSuggestions, setContactSuggestions] = useState<
    Record<number, UserSuggestion | null>
  >({});
  const lookupTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>(
    {},
  );

  // Project site coordinates: auto-filled from geocoding the project address,
  // overridable by clicking or dragging the marker on the map below.
  const [siteCoordinatesManual, setSiteCoordinatesManual] = useState(false);
  const geocodeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const siteCoordinates = localProject.infos.general.coordinates;
  const sitePosition: [number, number] | null = siteCoordinates
    ? [siteCoordinates.latitude, siteCoordinates.longitude]
    : null;

  const setSiteCoordinates = (coords: { latitude: number; longitude: number } | undefined) => {
    setLocalProject((prev) => updateNested(prev, "infos.general.coordinates", coords));
  };

  // Debounced auto-geocode on address change — but only while the user hasn't
  // dropped their own pin. Manual placement always wins.
  useEffect(() => {
    if (siteCoordinatesManual) return;
    if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
    const { address, city, postal } = localProject.infos.general;
    if (!address?.trim() && !city?.trim() && !postal?.trim()) return;
    geocodeTimer.current = setTimeout(async () => {
      const coords = await geocodeAddress(address, city, postal);
      if (coords && !siteCoordinatesManual) setSiteCoordinates(coords);
    }, 700);
    return () => {
      if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localProject.infos.general.address,
    localProject.infos.general.city,
    localProject.infos.general.postal,
    siteCoordinatesManual,
  ]);

  const handleMarkerPlaced = useCallback((lat: number, lng: number) => {
    setSiteCoordinates({ latitude: lat, longitude: lng });
    setSiteCoordinatesManual(true);
  // setLocalProject reference is stable; no closures over render state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Lets the "Placer un point au centre" button trigger a click event on
  // whatever location the user has panned to.
  const dropAtCenterRef = useRef<(() => void) | null>(null);
  const registerDropAtCenter = useCallback((fn: () => void) => {
    dropAtCenterRef.current = fn;
  }, []);

  const handleReGeocode = async () => {
    const { address, city, postal } = localProject.infos.general;
    const coords = await geocodeAddress(address, city, postal);
    if (coords) {
      setSiteCoordinates(coords);
      setSiteCoordinatesManual(false);
    } else {
      showNotification({
        type: "error",
        message: "Adresse introuvable — placez le point manuellement.",
      });
    }
  };

  const renderSiteMap = () => (
    <div className="flex flex-col gap-2 mt-4">
      <div className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div className="text-h4 text-neutral-500">
          Position du chantier{" "}
          {sitePosition
            ? siteCoordinatesManual
              ? "(placée manuellement)"
              : "(auto)"
            : "(non définie)"}
        </div>
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            onClick={() => dropAtCenterRef.current?.()}
            className="text-h5 text-blue-600 hover:underline"
          >
            {sitePosition ? "Replacer au centre" : "Placer un point au centre"}
          </button>
          <button
            type="button"
            onClick={handleReGeocode}
            className="text-h5 text-blue-600 hover:underline"
          >
            Recentrer depuis l'adresse
          </button>
        </div>
      </div>
      <div
        className="relative rounded-lg overflow-hidden border border-neutral-200"
        style={{ height: 260 }}
      >
        <MapContainer
          center={sitePosition || FRANCE_CENTER}
          zoom={sitePosition ? 14 : 5}
          style={{ width: "100%", height: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapRecenter position={sitePosition} />
          <MapClickHandler onClick={handleMarkerPlaced} />
          <MapCenterDropper registerDrop={registerDropAtCenter} />
          {sitePosition && (
            <Marker
              position={sitePosition}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const { lat, lng } = (e.target as L.Marker).getLatLng();
                  handleMarkerPlaced(lat, lng);
                },
              }}
            />
          )}
        </MapContainer>
        {!sitePosition && (
          <div className="pointer-events-none absolute inset-x-2 top-2 text-h5 text-white bg-black/60 rounded-md px-2 py-1 text-center">
            Cliquez n'importe où sur la carte pour placer le chantier — pas
            besoin d'adresse.
          </div>
        )}
      </div>
      <div className="text-h5 text-neutral-400">
        Cliquez sur la carte, déplacez le marqueur, ou utilisez le bouton
        « Placer un point au centre » pour positionner le chantier sans adresse.
      </div>
    </div>
  );

  // Fetch billing templates on mount
  useEffect(() => {
    if (userData?.org_id) {
      billingTemplatesApi.list(userData.org_id).then((data) => {
        setBillingTemplates(data);
        // Auto-select: prefer mapping match, otherwise auto-select if only one template
        if (data.length > 0) {
          const matched = selectedTemplateId
            ? data.find((bt) => bt.mappings[selectedTemplateId])
            : undefined;
          if (matched) {
            setSelectedBillingTemplateId(matched.id);
          } else if (data.length === 1) {
            setSelectedBillingTemplateId(data[0].id);
          }
        }
      }).catch(() => {});
    }
  }, [userData?.org_id]);

  // Resolve billing data from template + mapping
  const resolveBilling = (): ProjectBilling | undefined => {
    if (!selectedBillingTemplateId || !montantTotal) return undefined;
    const bt = billingTemplates.find((b) => b.id === selectedBillingTemplateId);
    if (!bt) return undefined;

    const total = parseFloat(montantTotal) || 0;
    const mapping = selectedTemplateId ? bt.mappings[selectedTemplateId] : undefined;

    return {
      billing_template_id: bt.id,
      montant_total: total,
      steps: bt.steps.map((step, index) => {
        // Resolve linked controlpoint IDs from template mapping
        const stepMapping = mapping?.find((m) => m.step_index === index);
        const pct = billingPctOverrides[index] !== undefined ? (parseFloat(billingPctOverrides[index]) || 0) : step.percentage;
        return {
          label: billingLabelOverrides[index] !== undefined ? billingLabelOverrides[index] : step.label,
          abbreviation: step.abbreviation || undefined,
          percentage: pct,
          montant: Math.round((total * pct) / 100 * 100) / 100,
          is_demarrage: step.is_demarrage,
          linked_step_ids: stepMapping?.linked_steps || [],
          status: "pending" as const,
          payment_delay_days: step.payment_delay_days ?? 15,
          project_delay_days: step.project_delay_days || undefined,
          step_gap_delay_days: step.step_gap_delay_days || undefined,
        };
      }),
    };
  };

  const handleEmailChange = (index: number, value: string) => {
    setLocalProject((prev) =>
      updateNestedArray(prev, "infos.client.contacts", index, {
        email: value,
        user_id: undefined,
      }),
    );
    setContactSuggestions((prev) => ({ ...prev, [index]: null }));
    if (lookupTimers.current[index]) clearTimeout(lookupTimers.current[index]);
    if (!value.includes("@") || !value.includes(".")) return;
    lookupTimers.current[index] = setTimeout(async () => {
      const result = await usersApi.lookup(value);
      setContactSuggestions((prev) => ({ ...prev, [index]: result }));
    }, 400);
  };

  const handleLinkContact = (index: number) => {
    const suggestion = contactSuggestions[index];
    if (!suggestion) return;
    const contact = localProject.infos.client.contacts[index];
    const nameParts = suggestion.display_name.split(" ");
    setLocalProject(
      updateNestedArray(localProject, "infos.client.contacts", index, {
        user_id: suggestion.id,
        first_name: contact.first_name || nameParts[0] || "",
        last_name: contact.last_name || nameParts.slice(1).join(" ") || "",
      }),
    );
    setContactSuggestions((prev) => ({ ...prev, [index]: null }));
  };

  const handleCoverPhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      showNotification({ type: "error", message: "Format invalide. Acceptés : JPG, PNG, WEBP" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification({ type: "error", message: "La taille dépasse 5MB" });
      return;
    }
    setCoverPhoto(file);
    setCoverPhotoPreview(URL.createObjectURL(file));
  };

  async function handleSave() {
    // The project name follows the primary client (no manual name field), so
    // requiring a non-empty derived name is equivalent to requiring the client
    // name (last name, or SCI name for an SCI).
    if (!localProject.infos.general.name.trim()) {
      showNotification({
        type: "error",
        message: "Le nom du client est obligatoire",
      });
      return;
    }

    // Validate billing percentages if a template is selected
    if (selectedBillingTemplateId && montantTotal) {
      const bt = billingTemplates.find((b) => b.id === selectedBillingTemplateId);
      if (bt) {
        const totalPct = bt.steps.reduce((sum, step, idx) => {
          const pct = billingPctOverrides[idx] !== undefined ? (parseFloat(billingPctOverrides[idx]) || 0) : step.percentage;
          return sum + pct;
        }, 0);
        if (Math.abs(totalPct - 100) > 0.01) {
          showNotification({
            type: "error",
            message: `Le total des pourcentages de facturation doit être égal à 100% (actuellement ${Math.round(totalPct * 100) / 100}%)`,
          });
          return;
        }
      }
    }

    setIsSaving(true);
    try {
      // Use the map's coordinates (either user-placed or the last auto-geocode).
      // Fall back to one more geocode attempt if the map never received coords
      // (e.g. the user typed the address and saved before the debounce fired).
      let coordinates = localProject.infos.general.coordinates;
      if (!coordinates) {
        coordinates =
          (await geocodeAddress(
            localProject.infos.general.address,
            localProject.infos.general.city,
            localProject.infos.general.postal,
          )) || undefined;
      }

      const billing = resolveBilling();

      const createdProject = await projectsApi.create({
        name: localProject.infos.general.name,
        num: localProject.infos.general.num,
        org_id: userData?.org_id || "org1",
        status: localProject.status || "in_progress",
        address: localProject.infos.general.address,
        city: localProject.infos.general.city,
        postal: localProject.infos.general.postal,
        notes: localProject.infos.general.notes,
        home_address: localProject.infos.general.home_address,
        home_city: localProject.infos.general.home_city,
        home_postal: localProject.infos.general.home_postal,
        lot: localProject.infos.general.lot,
        section: localProject.infos.general.section,
        cadastre_number: localProject.infos.general.cadastre_number,
        contacts: localProject.infos.client.contacts,
        partners: localProject.infos.partners,
        team: localProject.infos.team,
        themes: localProject.themes,
        folders: localProject.folders,
        billing,
        project_template_id: selectedTemplateId || undefined,
      });

      if (coordinates) {
        await projectsApi.update(createdProject.id, { coordinates });
        createdProject.infos.general.coordinates = coordinates;
      }

      // Upload cover photo if selected
      if (coverPhoto) {
        try {
          const compressedCover = await compressImage(coverPhoto);
          const [uploaded] = await projectsApi.uploadFiles(createdProject.id, [compressedCover]);
          const photoFile = { id: uploaded.id, url: uploaded.url, name: uploaded.name, octets: uploaded.size, created_at: Timestamp.now() };
          await projectsApi.update(createdProject.id, { photo_file: photoFile });
          createdProject.infos.general.photo_file = photoFile;
        } catch (e) {
          console.error("Failed to upload cover photo:", e);
        }
      }

      addProject(createdProject);

      const allContacts = localProject.infos.client.contacts;
      const newContacts = allContacts.filter(
        (c) => !c.user_id && c.email?.trim(),
      );

      // For new contacts, look up or invite to get a user_id
      const contactsToInvite: typeof newContacts = [];
      for (const contact of newContacts) {
        const existingUser = await usersApi.lookup(contact.email!);
        if (existingUser) {
          contact.user_id = existingUser.id;
        } else {
          contactsToInvite.push(contact);
        }
      }

      // Send invitations for brand-new contacts (assigns a user_id)
      if (contactsToInvite.length > 0) {
        const invitationResults = await Promise.allSettled(
          contactsToInvite.map((contact) =>
            invitationsApi.create({
              invitee_email: contact.email!,
              invitee_name:
                `${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
                undefined,
              invitation_type: "web",
              context: {
                access_type: "GUEST",
                project_id: createdProject.id,
              },
            }),
          ),
        );
        invitationResults.forEach((result, i) => {
          if (result.status === "fulfilled" && result.value.user_id) {
            contactsToInvite[i].user_id = result.value.user_id;
          }
        });
      }

      // Authoritative guest_user_ids: every contact that has a user_id.
      // Backend uses SET (not ArrayUnion), so removed contacts stay removed.
      const guestUserIds = allContacts
        .filter((c) => !!c.user_id)
        .map((c) => c.user_id!);

      await projectsApi.update(createdProject.id, {
        guest_user_ids: guestUserIds,
        infos: { ...createdProject.infos, client: { contacts: allContacts } },
      });

      // Seed the Décennales folder immediately from the partners' best
      // décennales — no need to wait for a planning to exist.
      try {
        const { folders: syncedFolders, warnings } = syncDecennalesFolder(
          createdProject,
          partners,
        );
        await projectsApi.update(createdProject.id, { folders: syncedFolders });
        warnings.forEach((w) =>
          showNotification({ type: "error", message: w }),
        );
      } catch (e) {
        console.error("Failed to seed décennales folder:", e);
      }

      showNotification({
        type: "success",
        message: "Projet créé avec succès",
      });
      onClose();
      navigateTo({ route: `/project/${createdProject.id}` });
    } catch (error) {
      console.error("Error creating project:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de la création du projet",
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <BaseModal
      title="Créer un projet"
      startIcon={<Icon1 name="cube" size={20}></Icon1>}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        <ChunkyTabs
          short_tabs={["Général", "Adresses", "Equipe", "Artisans", "Photo", "Facturation"]}
          setActiveTab={(tab) => setActiveTab(tab)}
          activeTab={activeTab}
        ></ChunkyTabs>
        <div className="min-h-[200px] max-h-[450px] overflow-y-auto flex flex-col p-1 gap-2">
          {activeTab === "Général" && (
            <>
              <DropDown
                label="Modèle de projet"
                category="Modèle"
                options={[
                  { value: "", label: "Aucun (projet vide)" },
                  ...[...projectTemplates].sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).map((t) => ({
                    value: t.id,
                    label: t.infos.general.name,
                  })),
                ]}
                value={selectedTemplateId}
                onSelect={(val) => {
                  const id = val as string;
                  setSelectedTemplateId(id);
                  const template = projectTemplates.find((t) => t.id === id);
                  if (template) {
                    setLocalProject((prev) => ({
                      ...prev,
                      themes: structuredClone(template.themes),
                      folders: structuredClone(template.folders),
                    }));
                  } else {
                    setLocalProject((prev) => ({
                      ...prev,
                      themes: structuredClone(default_themes),
                      folders: structuredClone(default_folders),
                    }));
                  }
                  // Auto-select matching billing template
                  if (id) {
                    const matched = billingTemplates.find((bt) => bt.mappings[id]);
                    setSelectedBillingTemplateId(matched?.id || "");
                  } else {
                    setSelectedBillingTemplateId("");
                  }
                }}
              />
              <div className="text-h3 font-medium text-neutral-500 mt-3">Contact principal</div>
              <DropDown
                label="Civilité"
                options={[
                  { value: "M.", label: "M." },
                  { value: "Mme", label: "Mme" },
                  { value: "SCI", label: "SCI" },
                ]}
                value={localProject.infos.client.contacts[0]?.prefix || "M."}
                onSelect={(value) => {
                  const contacts = [...localProject.infos.client.contacts];
                  const existing = contacts[0] || { first_name: "", last_name: "", prefix: "M." as const };
                  contacts[0] = { ...existing, prefix: value as ContactInfos["prefix"], sci_name: value === "SCI" ? existing.sci_name : undefined };
                  // The project has no manual name — it follows the primary
                  // client (SCI name when SCI, otherwise the last name), the
                  // same rule the Modify modal applies.
                  const name = value === "SCI" ? (existing.sci_name || "") : existing.last_name;
                  setLocalProject({
                    ...localProject,
                    infos: {
                      ...localProject.infos,
                      client: { ...localProject.infos.client, contacts },
                      general: { ...localProject.infos.general, name },
                    },
                  });
                }}
              />
              {localProject.infos.client.contacts[0]?.prefix === "SCI" && (
                <TextField
                  label="Nom de la SCI"
                  placeholder="Nom de la SCI"
                  value={localProject.infos.client.contacts[0]?.sci_name || ""}
                  onChange={(value) => {
                    const contacts = [...localProject.infos.client.contacts];
                    contacts[0] = { ...contacts[0], sci_name: value };
                    setLocalProject({
                      ...localProject,
                      infos: {
                        ...localProject.infos,
                        client: { ...localProject.infos.client, contacts },
                        general: { ...localProject.infos.general, name: value },
                      },
                    });
                  }}
                />
              )}
              <div className="flex flex-row gap-2">
                <TextField
                  label="Nom"
                  placeholder="DUPONT"
                  mandatory={true}
                  value={localProject.infos.client.contacts[0]?.last_name || ""}
                  onChange={(value) => {
                    const contacts = [...localProject.infos.client.contacts];
                    const existing = contacts[0] || { first_name: "", last_name: "", prefix: "M." as const };
                    contacts[0] = { ...existing, last_name: value };
                    const isSCI = existing.prefix === "SCI";
                    setLocalProject({
                      ...localProject,
                      infos: {
                        ...localProject.infos,
                        client: { ...localProject.infos.client, contacts },
                        general: { ...localProject.infos.general, ...(!isSCI && { name: value }) },
                      },
                    });
                  }}
                />
                <TextField
                  label="Prénom"
                  placeholder="Jean"
                  value={localProject.infos.client.contacts[0]?.first_name || ""}
                  onChange={(value) => {
                    const contacts = [...localProject.infos.client.contacts];
                    const existing = contacts[0] || { first_name: "", last_name: "", prefix: "M." as const };
                    contacts[0] = { ...existing, first_name: value };
                    setLocalProject({
                      ...localProject,
                      infos: { ...localProject.infos, client: { ...localProject.infos.client, contacts } },
                    });
                  }}
                />
              </div>
              <div className="flex flex-row gap-2">
                <div className="flex-1 min-w-0">
                  <TextField
                    label="Téléphone"
                    placeholder="06 01 02 03 04"
                    type="phone"
                    value={localProject.infos.client.contacts[0]?.phone_number || ""}
                    onChange={(value) => {
                      const contacts = [...localProject.infos.client.contacts];
                      const existing = contacts[0] || { first_name: "", last_name: "", prefix: "M." as const };
                      contacts[0] = { ...existing, phone_number: value };
                      setLocalProject({
                        ...localProject,
                        infos: { ...localProject.infos, client: { ...localProject.infos.client, contacts } },
                      });
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <TextField
                    label="Email"
                    placeholder="marie.curie@life.com"
                    value={localProject.infos.client.contacts[0]?.email || ""}
                    onChange={(value) => handleEmailChange(0, value)}
                  />
                  {localProject.infos.client.contacts[0]?.user_id && (
                    <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium px-1">
                      <span>✓</span>
                      <span>Compte existant lié</span>
                    </div>
                  )}
                  {contactSuggestions[0] && !localProject.infos.client.contacts[0]?.user_id && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="w-7 h-7 rounded-full bg-blue-200 flex items-center justify-center text-xs font-medium text-blue-700 shrink-0">
                        {contactSuggestions[0]!.display_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-neutral-800 truncate">
                          {contactSuggestions[0]!.display_name}
                        </div>
                        <div className="text-xs text-neutral-400">
                          Compte existant
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleLinkContact(0)}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 whitespace-nowrap"
                      >
                        Lier ce compte
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setContactSuggestions((prev) => ({
                            ...prev,
                            [0]: null,
                          }))
                        }
                        className="text-neutral-400 hover:text-neutral-600 text-sm leading-none"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {/* Additional contacts (index 1+) */}
              {localProject.infos.client.contacts.slice(1).map(
                (contact: ContactInfos, idx: number) => {
                  const index = idx + 1;
                  return (
                    <div
                      key={index}
                      className="flex flex-col gap-2 mb-4 p-4 rounded-xl border"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-h3 font-medium text-neutral-500">
                          Contact {index + 1}
                        </span>
                        <SecondaryButton
                          danger={true}
                          variant="small"
                          onClick={() =>
                            setLocalProject({
                              ...localProject,
                              infos: {
                                ...localProject.infos,
                                client: {
                                  ...localProject.infos.client,
                                  contacts:
                                    localProject.infos.client.contacts.filter(
                                      (_, i) => i !== index,
                                    ),
                                },
                              },
                            })
                          }
                        >
                          Supprimer
                        </SecondaryButton>
                      </div>
                      <DropDown
                        label="Civilité"
                        options={[
                          { value: "M.", label: "M." },
                          { value: "Mme", label: "Mme" },
                          { value: "SCI", label: "SCI" },
                        ]}
                        value={contact.prefix || "M."}
                        onSelect={(value) =>
                          setLocalProject(
                            updateNestedArray(
                              localProject,
                              "infos.client.contacts",
                              index,
                              {
                                prefix: value as ContactInfos["prefix"],
                                sci_name: value === "SCI" ? contact.sci_name : undefined,
                              },
                            ),
                          )
                        }
                      />
                      {contact.prefix === "SCI" && (
                        <TextField
                          label="Nom de la SCI"
                          placeholder="Nom de la SCI"
                          value={contact.sci_name || ""}
                          onChange={(value) =>
                            setLocalProject(
                              updateNestedArray(
                                localProject,
                                "infos.client.contacts",
                                index,
                                { sci_name: value },
                              ),
                            )
                          }
                        />
                      )}
                      <div className="flex flex-row gap-2">
                        <TextField
                          label="Nom"
                          placeholder="Dupont"
                          value={contact.last_name || ""}
                          onChange={(value) =>
                            setLocalProject(
                              updateNestedArray(
                                localProject,
                                "infos.client.contacts",
                                index,
                                { last_name: value },
                              ),
                            )
                          }
                        />
                        <TextField
                          label="Prénom"
                          placeholder="Jean"
                          value={contact.first_name || ""}
                          onChange={(value) =>
                            setLocalProject(
                              updateNestedArray(
                                localProject,
                                "infos.client.contacts",
                                index,
                                { first_name: value },
                              ),
                            )
                          }
                        />
                      </div>
                      <div className="flex flex-row gap-2">
                        <div className="flex-1 min-w-0">
                          <TextField
                            label="Téléphone"
                            placeholder="06 01 02 03 04"
                            type="phone"
                            value={contact.phone_number || ""}
                            onChange={(value) =>
                              setLocalProject(
                                updateNestedArray(
                                  localProject,
                                  "infos.client.contacts",
                                  index,
                                  { phone_number: value },
                                ),
                              )
                            }
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <TextField
                            label="Email"
                            placeholder="marie.curie@life.com"
                            value={contact.email || ""}
                            onChange={(value) => handleEmailChange(index, value)}
                          />
                        </div>
                      </div>
                      {contact.user_id && (
                        <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium px-1">
                          <span>✓</span>
                          <span>Compte existant lié</span>
                        </div>
                      )}
                      {contactSuggestions[index] && !contact.user_id && (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
                          <div className="w-7 h-7 rounded-full bg-blue-200 flex items-center justify-center text-xs font-medium text-blue-700 shrink-0">
                            {contactSuggestions[index]!.display_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-neutral-800 truncate">
                              {contactSuggestions[index]!.display_name}
                            </div>
                            <div className="text-xs text-neutral-400">
                              Compte existant
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleLinkContact(index)}
                            className="text-xs font-medium text-blue-600 hover:text-blue-800 whitespace-nowrap"
                          >
                            Lier ce compte
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setContactSuggestions((prev) => ({
                                ...prev,
                                [index]: null,
                              }))
                            }
                            className="text-neutral-400 hover:text-neutral-600 text-sm leading-none"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                  );
                },
              )}
              <SecondaryButton
                startIcon={<Icon1 name="plus" size={18}></Icon1>}
                label="Ajouter un contact"
                variant="large"
                onClick={() => setShowCreateContact(true)}
              />
              {showCreateContact && (
                <CreateContactModal
                  onClose={() => setShowCreateContact(false)}
                  onSave={(newContact) => {
                    setLocalProject((prev) => {
                      const existing = prev.infos.client.contacts[0];
                      const principalIsEmpty = !existing?.last_name && !existing?.first_name && !existing?.email;
                      const contacts = principalIsEmpty
                        ? [newContact, ...prev.infos.client.contacts.slice(1)]
                        : [...prev.infos.client.contacts, newContact];
                      return {
                        ...prev,
                        infos: {
                          ...prev.infos,
                          client: { ...prev.infos.client, contacts },
                        },
                      };
                    });
                  }}
                />
              )}
              <AreaTextField
                label="Notes"
                placeholder="Notes sur le projet"
                value={localProject.infos.general.notes || ""}
                onChange={(value) =>
                  setLocalProject(
                    updateNested(localProject, "infos.general.notes", value),
                  )
                }
              />
              <div className="text-h5 text-neutral-300 italic -mt-1">
                visible uniquement en interne
              </div>
            </>
          )}
          {activeTab === "Adresses" && (
            <>
              <div className="text-h3 font-medium text-neutral-500">Domicile</div>
              <TextField
                label="Adresse"
                placeholder="1 rue des fleurs"
                value={localProject.infos.general.home_address || ""}
                onChange={(value) =>
                  setLocalProject(updateNested(localProject, "infos.general.home_address", value))
                }
              />
              <div className="flex flex-row gap-2">
                <TextField
                  label="Code postal"
                  placeholder="35000"
                  value={localProject.infos.general.home_postal || ""}
                  onChange={(value) =>
                    setLocalProject(updateNested(localProject, "infos.general.home_postal", value))
                  }
                />
                <TextField
                  label="Ville"
                  placeholder="Rennes"
                  value={localProject.infos.general.home_city || ""}
                  onChange={(value) =>
                    setLocalProject(updateNested(localProject, "infos.general.home_city", value))
                  }
                />
              </div>

              <div className="text-h3 font-medium text-neutral-500 mt-4">Adresse du projet</div>
              <TextField
                label="Adresse"
                placeholder="1 rue des fleurs"
                value={localProject.infos.general.address || ""}
                onChange={(value) =>
                  setLocalProject(updateNested(localProject, "infos.general.address", value))
                }
              />
              <div className="flex flex-row gap-2">
                <TextField
                  label="Code postal"
                  placeholder="35000"
                  value={localProject.infos.general.postal || ""}
                  onChange={(value) =>
                    setLocalProject(updateNested(localProject, "infos.general.postal", value))
                  }
                />
                <TextField
                  label="Ville"
                  placeholder="Rennes"
                  value={localProject.infos.general.city || ""}
                  onChange={(value) =>
                    setLocalProject(updateNested(localProject, "infos.general.city", value))
                  }
                />
              </div>
              {renderSiteMap()}
              <div className="flex flex-row gap-2">
                <TextField
                  label="Lot"
                  placeholder="Lot 12"
                  value={localProject.infos.general.lot || ""}
                  onChange={(value) =>
                    setLocalProject(updateNested(localProject, "infos.general.lot", value))
                  }
                />
                <TextField
                  label="Section"
                  placeholder="AB"
                  value={localProject.infos.general.section || ""}
                  onChange={(value) =>
                    setLocalProject(updateNested(localProject, "infos.general.section", value))
                  }
                />
                <TextField
                  label="N° cadastre"
                  placeholder="1234"
                  value={localProject.infos.general.cadastre_number || ""}
                  onChange={(value) =>
                    setLocalProject(updateNested(localProject, "infos.general.cadastre_number", value))
                  }
                />
              </div>
            </>
          )}
          {activeTab === "Equipe" && (
            <>
              <MemberAssignmentBlock
                team={localProject.infos.team}
                defaultAllVisible
                onTeamChange={(team) =>
                  setLocalProject({
                    ...localProject,
                    infos: { ...localProject.infos, team },
                  })
                }
              />
            </>
          )}
          {activeTab === "Artisans" && (
            <>
              {modifyPartner && (
                <ModifyPartnerModal
                  initialPartner={modifyPartner}
                  onClose={() => setModifyPartnerId(null)}
                />
              )}
              {decennalePartner && (
                <AddDecennaleModal
                  partnerId={decennalePartner.id}
                  partnerName={decennalePartner.name}
                  onClose={() => setDecennalePartner(null)}
                />
              )}
              {addPartnerForSection !== false && (
                <AddPartnerToProjectModal
                  onClose={() => setAddPartnerForSection(false)}
                  filterByType={addPartnerForSection || undefined}
                  categoryOptions={PROJECT_PARTNER_CATEGORY_OPTIONS}
                  onSelect={(partner: PartnerInfos) => {
                    // Global "Ajouter un intervenant" sets addPartnerForSection
                    // to "" — the modal's category picker writes the chosen
                    // section into partner.company_types, so fall back to that.
                    const sectionKey = addPartnerForSection || partner.company_types?.[0];
                    setLocalProject((prev) => {
                      const existing = prev.infos.partners.find((p) => p.id === partner.id);
                      if (existing) {
                        // Partner already in project — add this section (and its
                        // materialized expansions) to their types.
                        if (sectionKey) {
                          const merged = addSectionToCompanyTypes(existing.company_types, sectionKey);
                          if (merged.length !== (existing.company_types?.length || 0)) {
                            const updated = prev.infos.partners.map((p) =>
                              p.id === partner.id ? { ...p, company_types: merged } : p,
                            );
                            return { ...prev, infos: { ...prev.infos, partners: updated } };
                          }
                        }
                        return prev;
                      }
                      // New partner — strip down to project-scoped fields
                      // (id, role, display_name, company_types) so the live
                      // partner data is fetched at render time.
                      const stripped = stripPartnerSnapshot(partner);
                      const partnerWithSection = sectionKey
                        ? { ...stripped, company_types: addSectionToCompanyTypes(stripped.company_types, sectionKey) }
                        : stripped;
                      return {
                        ...prev,
                        infos: {
                          ...prev.infos,
                          partners: [...prev.infos.partners, partnerWithSection],
                        },
                      };
                    });
                    setAddPartnerForSection(false);
                  }}
                  existingPartnerIds={addPartnerForSection
                    ? localProject.infos.partners
                        .filter((p) => p.company_types?.includes(addPartnerForSection))
                        .map((p) => p.id)
                    : []}
                />
              )}
              <div className="flex flex-col gap-1">
                {/* Header row */}
                <div className="flex flex-row items-center justify-between px-4 pb-1">
                  <div className="flex-1" />
                  <div className="text-h4 text-neutral-400 text-right" style={{ minWidth: 180 }}>
                    Décennales
                  </div>
                </div>
                {PROJECT_PARTNER_CATEGORY_OPTIONS.map((type) => {
                  const hydrated = resolveProjectPartners(localProject, partners);
                  // Direct membership only — expansions are materialized into
                  // company_types at add time (see addSectionToCompanyTypes).
                  const candidates = hydrated
                    .filter((p) => p.company_types?.includes(type.value))
                    .sort((a, b) => (a.company_types?.length || 0) - (b.company_types?.length || 0));
                  const projectDateObj = localProject.infos.general.creation_date?.toDate
                    ? localProject.infos.general.creation_date.toDate()
                    : localProject.infos.general.creation_date
                      ? new Date((localProject.infos.general.creation_date as any)?.seconds * 1000)
                      : new Date();
                  const projectDateIso = projectDateObj.toISOString().slice(0, 10);
                  const getDecennaleStatus = (partnerId: string) => {
                    const storePartner = partners.find((p) => p.id === partnerId);
                    return (storePartner?.decennales || []).some((d) => {
                      if (d.start_date && d.end_date) return projectDateIso >= d.start_date && projectDateIso <= d.end_date;
                      if (d.year) return projectDateObj.getFullYear() === d.year;
                      return false;
                    }) ? "valid" : "missing";
                  };
                  if (candidates.length === 0) {
                    return (
                      <div
                        key={type.value}
                        className="flex flex-row items-center justify-between px-4 py-2.5 rounded-lg border transition-colors bg-neutral-50 border-neutral-200 cursor-pointer hover:bg-neutral-100"
                        onClick={() => setAddPartnerForSection(type.value)}
                      >
                        <div className="flex flex-row items-center gap-2 flex-1">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-neutral-300" />
                          <div className="flex flex-col">
                            <div className="text-h3 font-medium text-neutral-400">
                              {type.label.toUpperCase()}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-row items-center gap-2">
                          <Icon1 name="plus" size={14} color="grey" />
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={type.value} className="flex flex-col gap-1">
                      {candidates.map((assigned) => (
                        <div
                          key={`${type.value}-${assigned.id}`}
                          className="flex flex-row items-center justify-between px-4 py-2.5 rounded-lg border transition-colors bg-white border-neutral-200"
                        >
                          <div className="flex flex-row items-center gap-2 flex-1">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-neutral-800" />
                            <div className="flex flex-col">
                              <div className="text-h3 font-medium text-black">
                                {type.label.toUpperCase()}
                              </div>
                              <div className="text-h4 text-neutral-400">
                                {assigned.company_name || assigned.display_name}
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-row items-center gap-2">
                            <SecondaryButton
                              danger={true}
                              variant="small"
                              onClick={() => {
                                const updated = localProject.infos.partners
                                  .map((p) => {
                                    if (p.id !== assigned.id) return p;
                                    const remaining = (p.company_types || []).filter(
                                      (t) => t !== type.value,
                                    );
                                    return remaining.length > 0
                                      ? { ...p, company_types: remaining }
                                      : null;
                                  })
                                  .filter(Boolean) as typeof localProject.infos.partners;
                                setLocalProject({
                                  ...localProject,
                                  infos: {
                                    ...localProject.infos,
                                    partners: updated,
                                  },
                                });
                              }}
                            >
                              Retirer
                            </SecondaryButton>
                            {assigned.id !== "maitre_ouvrage" && (() => {
                              const status = getDecennaleStatus(assigned.id);
                              if (status === "valid") {
                                return (
                                  <button
                                    onClick={() => setModifyPartnerId(assigned.id)}
                                    className="flex items-center gap-1 text-neutral-500 hover:text-neutral-700 cursor-pointer"
                                    title="Décennale valide"
                                  >
                                    <Icon1 name="pdf" size={22} />
                                  </button>
                                );
                              }
                              return (
                                <SecondaryButton
                                  danger={true}
                                  variant="small"
                                  onClick={() => setDecennalePartner({ id: assigned.id, name: assigned.company_name || assigned.display_name })}
                                >
                                  Ajouter une décennale
                                </SecondaryButton>
                              );
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
              <SecondaryButton
                startIcon={<Icon1 name="plus" size={16} />}
                label="Ajouter un intervenant"
                variant="large"
                onClick={() => setAddPartnerForSection("")}
              />
            </>
          )}
          {activeTab === "Photo" && (
            <>
              <div className="text-h3 font-medium text-neutral-500">
                Photo de façade
              </div>
              <div className="text-h5 text-neutral-400">
                Cette photo sera utilisée comme couverture du projet.
              </div>
              {coverPhotoPreview ? (
                <div className="flex flex-col gap-2">
                  <img
                    src={coverPhotoPreview}
                    alt="Aperçu"
                    className="w-full max-h-64 object-cover rounded-lg border"
                  />
                  <SecondaryButton
                    startIcon={<Icon1 name="delete" size={18} />}
                    label="Supprimer la photo"
                    variant="large"
                    onClick={() => {
                      setCoverPhoto(null);
                      setCoverPhotoPreview(null);
                      if (coverPhotoInputRef.current) coverPhotoInputRef.current.value = "";
                    }}
                  />
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-neutral-200 rounded-xl p-10 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-neutral-400 transition-colors"
                  onClick={() => coverPhotoInputRef.current?.click()}
                >
                  <Icon1 name="join" size={24} color="grey" />
                  <span className="text-h3 text-neutral-400">
                    Cliquez pour ajouter une photo
                  </span>
                  <span className="text-h5 text-neutral-300">
                    JPG, PNG, WEBP — 5MB max
                  </span>
                </div>
              )}
              <input
                ref={coverPhotoInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleCoverPhotoChange}
                className="hidden"
              />
            </>
          )}
          {activeTab === "Facturation" && (
            <>
              <div className="text-h3 font-medium text-neutral-500">
                <div className="flex flex-row items-center gap-1.5">
                  <span>Modèle de facturation</span>
                  <HelpTooltip content="Définit les étapes de facturation et leurs pourcentages par défaut. Changer de modèle réinitialise les pourcentages des étapes non encore émises ; les étapes déjà facturées ne sont pas modifiées." />
                </div>
              </div>
              <div className="text-h5 text-neutral-400">
                Sélectionnez un modèle de facturation et entrez le montant total HT.
              </div>
              <DropDown
                label="Modèle de facturation"
                category="Facturation"
                options={[
                  { value: "", label: "Aucun" },
                  ...billingTemplates
                    .filter((bt) => !selectedTemplateId || bt.mappings[selectedTemplateId])
                    .map((bt) => ({ value: bt.id, label: bt.name })),
                ]}
                value={selectedBillingTemplateId}
                onSelect={(val) => { setSelectedBillingTemplateId(val as string); setBillingPctOverrides({}); setBillingLabelOverrides({}); }}
              />
              {selectedBillingTemplateId && (
                <>
                  <TextField
                    label="Honoraires HT (€)"
                    value={montantTotal}
                    // Accept the French decimal comma: translate "," → "." (and
                    // drop spaces) so the full amount is parsed, not truncated.
                    onChange={(v) => setMontantTotal(v.replace(/,/g, ".").replace(/\s/g, ""))}
                    placeholder="150000"
                  />
                  {(() => {
                    const bt = billingTemplates.find((b) => b.id === selectedBillingTemplateId);
                    if (!bt) return null;
                    const totalHT = parseFloat(montantTotal) || 0;
                    const TVA = 0.2;

                    const getStepPct = (s: typeof bt.steps[0]) => {
                      const idx = bt.steps.indexOf(s);
                      return billingPctOverrides[idx] !== undefined ? (parseFloat(billingPctOverrides[idx]) || 0) : s.percentage;
                    };
                    const demarrageSteps = bt.steps.filter((s) => s.is_demarrage);
                    const avancementSteps = bt.steps.filter((s) => !s.is_demarrage);
                    const demarragePct = demarrageSteps.reduce((sum, s) => sum + getStepPct(s), 0);
                    const avancementPctTotal = avancementSteps.reduce((sum, s) => sum + getStepPct(s), 0);
                    const totalPct = demarragePct + avancementPctTotal;
                    const demarrageHT = Math.round((totalHT * demarragePct) / 100 * 100) / 100;
                    const resteHT = totalHT - demarrageHT;

                    const fmt = (v: number) => v.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
                    const ttc = (ht: number) => Math.round(ht * (1 + TVA) * 100) / 100;

                    if (!totalHT) return null;

                    return (
                      <div className="flex flex-col mt-2">
                        {/* Column headers */}
                        <div className="flex flex-row items-center px-2 py-1 text-h5 text-neutral-400">
                          <span className="flex-1" />
                          <span className="w-16 text-right">%</span>
                          <span className="w-24 text-right">HT</span>
                          <span className="w-24 text-right">TTC</span>
                        </div>

                        {/* Démarrage group */}
                        {demarrageSteps.length > 0 && (
                          <>
                            <div className="flex flex-row items-center px-2 py-1.5 rounded-lg bg-green-50 mt-1">
                              <span className="text-h3 font-medium text-green-700 flex-1">Démarrage</span>
                              <span className="w-16 text-right text-h4 text-green-600">{Math.round(demarragePct * 100) / 100}%</span>
                              <span className="w-24 text-right text-h3 font-medium text-green-700">{fmt(demarrageHT)} €</span>
                              <span className="w-24 text-right text-h4 text-green-600">{fmt(ttc(demarrageHT))} €</span>
                            </div>
                            {demarrageSteps.map((step, i) => {
                              const idx = bt.steps.indexOf(step);
                              const pct = getStepPct(step);
                              const stepHT = Math.round((totalHT * pct) / 100 * 100) / 100;
                              return (
                                <div key={`d-${i}`} className="flex flex-row items-center px-2 pl-6 py-0.5">
                                  <input
                                    type="text"
                                    className="text-h4 text-neutral-500 flex-1 border border-neutral-200 rounded px-1 py-0.5 focus:outline-none focus:border-blue-400"
                                    value={billingLabelOverrides[idx] !== undefined ? billingLabelOverrides[idx] : step.label}
                                    onChange={(e) => setBillingLabelOverrides((prev) => ({ ...prev, [idx]: e.target.value }))}
                                  />
                                  <span className="w-16 text-right text-h5 text-neutral-400 flex items-center justify-end gap-0.5">
                                    <input
                                      type="number"
                                      className="w-12 text-right text-h5 text-neutral-500 border border-neutral-200 rounded px-1 py-0.5 focus:outline-none focus:border-blue-400"
                                      value={billingPctOverrides[idx] !== undefined ? billingPctOverrides[idx] : step.percentage}
                                      onChange={(e) => setBillingPctOverrides((prev) => ({ ...prev, [idx]: e.target.value }))}
                                    />%
                                  </span>
                                  <span className="w-24 text-right text-h4 text-neutral-500">{fmt(stepHT)} €</span>
                                  <span className="w-24 text-right text-h5 text-neutral-400">{fmt(ttc(stepHT))} €</span>
                                </div>
                              );
                            })}
                          </>
                        )}

                        {/* Avancement steps — percentages relative to reste */}
                        {avancementSteps.length > 0 && (
                          <>
                            <div className="flex flex-row items-center px-2 py-1.5 mt-2">
                              <span className="text-h4 text-neutral-400 flex-1">
                                Avancement — {fmt(resteHT)} € HT restant
                              </span>
                            </div>
                            {avancementSteps.map((step, i) => {
                              const idx = bt.steps.indexOf(step);
                              const pct = getStepPct(step);
                              const stepHT = Math.round((totalHT * pct) / 100 * 100) / 100;
                              return (
                                <div key={`a-${i}`} className="flex flex-row items-center px-2 py-1 rounded-lg bg-neutral-50">
                                  <input
                                    type="text"
                                    className="text-h3 font-medium flex-1 border border-neutral-200 rounded px-1 py-0.5 focus:outline-none focus:border-blue-400"
                                    value={billingLabelOverrides[idx] !== undefined ? billingLabelOverrides[idx] : step.label}
                                    onChange={(e) => setBillingLabelOverrides((prev) => ({ ...prev, [idx]: e.target.value }))}
                                  />
                                  <span className="w-16 text-right text-h4 text-neutral-500 flex items-center justify-end gap-0.5">
                                    <input
                                      type="number"
                                      className="w-12 text-right text-h4 text-neutral-500 border border-neutral-200 rounded px-1 py-0.5 focus:outline-none focus:border-blue-400"
                                      value={billingPctOverrides[idx] !== undefined ? billingPctOverrides[idx] : step.percentage}
                                      onChange={(e) => setBillingPctOverrides((prev) => ({ ...prev, [idx]: e.target.value }))}
                                    />%
                                  </span>
                                  <span className="w-24 text-right text-h3 font-medium text-neutral-700">{fmt(stepHT)} €</span>
                                  <span className="w-24 text-right text-h4 text-neutral-500">{fmt(ttc(stepHT))} €</span>
                                </div>
                              );
                            })}
                          </>
                        )}

                        {/* Total row */}
                        <div className="flex flex-row items-center px-2 py-1.5 border-t mt-2">
                          <span className="text-h3 font-semibold flex-1">Total</span>
                          <span className={`w-16 text-right text-h4 font-semibold ${Math.abs(totalPct - 100) > 0.01 ? "text-red-500" : "text-neutral-500"}`}>{Math.round(totalPct * 100) / 100}%</span>
                          <span className="w-24 text-right text-h3 font-semibold text-neutral-800">{fmt(totalHT)} €</span>
                          <span className="w-24 text-right text-h3 font-semibold text-neutral-800">{fmt(ttc(totalHT))} €</span>
                        </div>
                        {Math.abs(totalPct - 100) > 0.01 && (
                          <div className="text-h5 text-red-500 mt-1 px-2">
                            Le total des pourcentages doit être égal à 100%
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        {activeTab !== "Facturation" ? (
          <Button
            variant="small"
            label="Suivant"
            onClick={() => {
              const tabs = ["Général", "Adresses", "Equipe", "Artisans", "Photo", "Facturation"];
              const currentIndex = tabs.indexOf(activeTab);
              if (currentIndex < tabs.length - 1) {
                setActiveTab(tabs[currentIndex + 1]);
              }
            }}
          />
        ) : (
          <Button
            variant="small"
            label="Créer le projet"
            onClick={handleSave}
            loading={isSaving}
          />
        )}
      </div>
    </BaseModal>
  );
};
